// Use this to add dark mode support
// import { mode } from "@chakra-ui/theme-tools"
import type {
  SystemStyleObject,
  SystemStyleFunction,
} from '@chakra-ui/theme-tools'
import { mode, transparentize } from '@chakra-ui/theme-tools'

const baseStyle: SystemStyleObject = {
  lineHeight: '1.2',
  borderRadius: 'base',
  fontWeight: 'semibold',
  transitionProperty: 'common',
  transitionDuration: 'normal',
  _focus: {
    boxShadow: 'none',
  },
  _disabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  _hover: {
    _disabled: {
      bg: 'initial',
    },
  },
}

const variantGhost: SystemStyleFunction = props => {
  const { colorScheme: c } = props

  return {
    color: `${c}`,
    bg: 'transparent',
    _hover: null,
    _active: null,
  }
}

const variantOutline: SystemStyleFunction = props => {
  const { colorScheme: c } = props
  const borderColor = 'gray.200'
  return {
    border: '1px solid',
    borderColor: c === 'gray' ? borderColor : 'currentColor',
    ...variantGhost(props),
  }
}

interface AccessibleColor {
  bg?: string
  color?: string
  disabledBg?: string
  disabledColor?: string
}

/** Accessible color overrides for less accessible colors. */
const accessibleColorMap: { [key: string]: AccessibleColor } = {
  appBlue: {
    bg: 'appBlue',
    color: 'white',
    disabledBg: 'appGreyThree',
    disabledColor: 'appSlate',
  },
  appRed: {
    bg: 'appRed',
    color: 'white',
    disabledBg: 'appGreyThree',
    disabledColor: 'appSlate',
  },
  appBrick: {
    bg: 'appBrick',
    color: 'white',
    disabledBg: 'appGreyThree',
    disabledColor: 'appSlate',
  },
  appSemanticSearch: {
    bg: 'appWhite',
    color: 'appClearBlue',
    disabledBg: 'appGreyThree',
    disabledColor: 'appSlate',
  },
  appYellowOrange: {
    bg: 'appYellowOrange',
    color: 'white',
    disabledBg: 'appGreyThree',
    disabledColor: 'appSlate',
  },
}

const variantSolid: SystemStyleFunction = props => {
  const { colorScheme: c } = props

  const {
    bg = `${c}`,
    color = 'white',
    disabledBg,
    disabledColor,
  } = accessibleColorMap[c] ?? {}

  return {
    bg,
    color,
    _hover: null,
    _disabled: {
      bg: disabledBg,
      color: disabledColor,
    },
    _active: null,
  }
}

const variantLink: SystemStyleFunction = props => {
  const { colorScheme: c } = props
  return {
    padding: 0,
    height: 'auto',
    lineHeight: 'normal',
    verticalAlign: 'baseline',
    color: `${c}`,
    boxShadow: 'none',
    _hover: {
      textDecoration: 'none',
      _disabled: {
        textDecoration: 'none',
      },
    },
    _active: {
      color: `${c}`,
    },
  }
}

const variantLinkTwo: SystemStyleFunction = props => {
  const { colorScheme: c } = props
  return {
    padding: 0,
    height: 'auto',
    lineHeight: 'normal',
    verticalAlign: 'baseline',
    color: `#a0aec0`,
    boxShadow: 'none',
    _hover: {
      color: `${c}`,
    },
  }
}

const variantUnstyled: SystemStyleObject = {
  bg: 'none',
  color: 'inherit',
  display: 'inline',
  lineHeight: 'inherit',
  m: 0,
  p: 0,
}

const variantDeleteCTA: SystemStyleFunction = props => {
  props.colorScheme = 'appBrick'
  return {
    border: '1px solid',
    borderColor: 'currentColor',
    gap: 'var(--chakra-sizes-2)',
    ...variantGhost(props),
  }
}

const variants = {
  ghost: variantGhost,
  outline: variantOutline,
  solid: variantSolid,
  link: variantLink,
  linkTwo: variantLinkTwo,
  unstyled: variantUnstyled,
  deleteCTA: variantDeleteCTA,
}

const sizes: Record<string, SystemStyleObject> = {
  lg: {
    h: 12,
    minW: 12,
    fontSize: 'lg',
    px: 6,
  },
  md: {
    h: 10,
    minW: 10,
    fontSize: 'md',
    px: 4,
  },
  sm: {
    h: 8,
    minW: 8,
    fontSize: 'sm',
    px: 3,
  },
  xs: {
    h: 6,
    minW: 6,
    fontSize: 'xs',
    px: 2,
  },
}

const defaultProps = {
  variant: 'solid',
  size: 'md',
  colorScheme: 'appBlue',
}

export default {
  baseStyle,
  variants,
  sizes,
  defaultProps,
}
